/**
 * Comments Component
 * Displays and manages comments on blog posts
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiEdit2, FiTrash2, FiSend } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';

const Comments = ({ blogId }) => {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { showToast } = useToast();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/comments/${blogId}`);
      setComments(response.data.data || []);
    } catch (error) {
      // Error is handled by the UI (no comments displayed)
      // In production, this could be logged to an error tracking service
      showToast('Failed to load comments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!commentText.trim()) {
      showToast('Please enter a comment', 'error');
      return;
    }

    if (commentText.length > 2000) {
      showToast('Comment cannot exceed 2000 characters', 'error');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.post(`/comments/${blogId}`, {
        text: commentText.trim()
      });
      
      setComments([response.data.data, ...comments]);
      setCommentText('');
      showToast('Comment posted successfully', 'success');
    } catch (error) {
      // Error is handled by toast notification in the catch block
      showToast(error.response?.data?.error || 'Failed to post comment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim()) {
      showToast('Please enter a comment', 'error');
      return;
    }

    if (editText.length > 2000) {
      showToast('Comment cannot exceed 2000 characters', 'error');
      return;
    }

    try {
      const response = await api.put(`/comments/${commentId}`, {
        text: editText.trim()
      });
      
      setComments(comments.map(c => 
        c._id === commentId ? response.data.data : c
      ));
      setEditingId(null);
      setEditText('');
      showToast('Comment updated successfully', 'success');
    } catch (error) {
      // Error is handled by toast notification in the catch block
      showToast(error.response?.data?.error || 'Failed to update comment', 'error');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await api.delete(`/comments/${commentId}`);
      setComments(comments.filter(c => c._id !== commentId));
      showToast('Comment deleted successfully', 'success');
    } catch (error) {
      // Error is handled by toast notification in the catch block
      showToast(error.response?.data?.error || 'Failed to delete comment', 'error');
    }
  };

  const startEdit = (comment) => {
    setEditingId(comment._id);
    setEditText(comment.text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const canEditComment = (comment) => {
    return isAuthenticated && (user?.id === comment.user?._id || isAdmin);
  };

  const formatDate = (date) => {
    const commentDate = new Date(date);
    const now = new Date();
    const diffMs = now - commentDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return commentDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading comments...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Comments ({comments.length})</Title>
      </Header>

      {/* Comment form for authenticated users */}
      {isAuthenticated ? (
        <CommentForm onSubmit={handleSubmitComment}>
          <FormHeader>
            <UserAvatar>
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </UserAvatar>
            <UserName>Comment as {user?.name}</UserName>
          </FormHeader>
          <CommentTextarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Share your thoughts..."
            rows="4"
            maxLength="2000"
          />
          <FormFooter>
            <CharCount $warning={commentText.length > 1800}>
              {commentText.length}/2000
            </CharCount>
            <SubmitButton 
              type="submit" 
              disabled={submitting || !commentText.trim()}
            >
              {submitting ? 'Posting...' : (
                <>
                  <FiSend /> Post Comment
                </>
              )}
            </SubmitButton>
          </FormFooter>
        </CommentForm>
      ) : (
        <LoginPrompt>
          <LoginMessage>Please log in to leave a comment.</LoginMessage>
          <LoginButton href="/login">Log In</LoginButton>
        </LoginPrompt>
      )}

      {/* Comments list */}
      <CommentsList>
        {comments.length > 0 ? (
          <AnimatePresence>
            {comments.map((comment, index) => (
              <CommentItem
                key={comment._id}
                as={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <CommentHeader>
                  <AuthorInfo>
                    <CommentAvatar>
                      {comment.user?.name?.charAt(0).toUpperCase() || 'U'}
                    </CommentAvatar>
                    <div>
                      <AuthorName>
                        {comment.user?.name || 'Anonymous'}
                        {isAuthenticated && user && comment.user?._id === user._id && (
                          <LoggedInBadge title="You are logged in">✓</LoggedInBadge>
                        )}
                      </AuthorName>
                      <CommentDate>
                        {formatDate(comment.createdAt)}
                        {comment.isEdited && <EditedLabel> (edited)</EditedLabel>}
                      </CommentDate>
                    </div>
                  </AuthorInfo>
                  
                  {canEditComment(comment) && editingId !== comment._id && (
                    <CommentActions>
                      <ActionButton 
                        onClick={() => startEdit(comment)}
                        title="Edit comment"
                      >
                        <FiEdit2 />
                      </ActionButton>
                      <ActionButton 
                        onClick={() => handleDeleteComment(comment._id)}
                        $danger
                        title="Delete comment"
                      >
                        <FiTrash2 />
                      </ActionButton>
                    </CommentActions>
                  )}
                </CommentHeader>

                {editingId === comment._id ? (
                  <EditForm>
                    <CommentTextarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows="4"
                      maxLength="2000"
                      autoFocus
                    />
                    <EditActions>
                      <CharCount $warning={editText.length > 1800}>
                        {editText.length}/2000
                      </CharCount>
                      <EditButtonGroup>
                        <CancelButton onClick={cancelEdit}>Cancel</CancelButton>
                        <SaveButton onClick={() => handleEditComment(comment._id)}>
                          Save
                        </SaveButton>
                      </EditButtonGroup>
                    </EditActions>
                  </EditForm>
                ) : (
                  <CommentText>{comment.text}</CommentText>
                )}
              </CommentItem>
            ))}
          </AnimatePresence>
        ) : (
          <EmptyState>
            <EmptyIcon>💬</EmptyIcon>
            <EmptyText>No comments yet. Be the first to share your thoughts!</EmptyText>
          </EmptyState>
        )}
      </CommentsList>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 2px solid var(--border-color, rgba(255, 255, 255, 0.1));
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: var(--text-primary);
  margin: 0;
`;

const CommentForm = styled.form`
  background: var(--bg-secondary);
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--accent-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
`;

const UserName = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  background: var(--bg-primary);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  color: var(--text-primary);
  font-size: 1rem;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const CharCount = styled.div`
  font-size: 0.85rem;
  color: ${props => props.$warning ? '#ff6b6b' : 'var(--text-secondary)'};
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoginPrompt = styled.div`
  background: var(--bg-secondary);
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  margin-bottom: 2rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
`;

const LoginMessage = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const LoginButton = styled.a`
  display: inline-block;
  background: var(--accent-gradient);
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CommentItem = styled.div`
  background: var(--bg-secondary);
  padding: 1.5rem;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.1);
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CommentAvatar = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: var(--accent-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const AuthorName = styled.div`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LoggedInBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--success-color, #10b981);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
`;

const CommentDate = styled.div`
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const EditedLabel = styled.span`
  font-style: italic;
`;

const CommentActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.$danger ? '#ff6b6b' : 'var(--text-secondary)'};
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.$danger ? 'rgba(255, 107, 107, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.$danger ? '#ff4757' : 'var(--accent)'};
  }

  svg {
    font-size: 1rem;
  }
`;

const CommentText = styled.p`
  color: var(--text-primary);
  line-height: 1.6;
  font-size: 1rem;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EditForm = styled.div`
  margin-top: 1rem;
`;

const EditActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const CancelButton = styled.button`
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: var(--text-secondary);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    color: var(--text-primary);
  }
`;

const SaveButton = styled.button`
  background: var(--accent-gradient);
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-secondary);
  font-size: 1rem;
`;

const EditButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

Comments.propTypes = {
  blogId: PropTypes.string.isRequired
};

export default Comments;
